import React from 'react';
import {
  Container,
  Typography,
  Box,
  Breadcrumbs,
  Link,
  CircularProgress,
  Chip,
  Button,
  Alert,
  Tooltip
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridToolbar,
  GridRenderCellParams
} from '@mui/x-data-grid';
import { Home, Schedule, Refresh } from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useCurrentUser } from '../../hooks/useApi';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';

interface ScheduledTaskLastRun {
  id: number;
  status: 'running' | 'completed' | 'failed' | 'skipped';
  startedAt: string;
  completedAt: string | null;
  durationMs: number | null;
  errorMessage: string | null;
  metadata: string | null;
}

interface ScheduledTask {
  functionName: string;
  appName: string;
  description: string;
  schedule: string;
  scheduleDescription: string;
  lastRun: ScheduledTaskLastRun | null;
  nextRun: string;
  isEnabled: boolean;
}

const formatDuration = (ms: number | null): string => {
  if (ms === null || ms === undefined) return '-';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  const minutes = Math.floor(ms / 60000);
  const seconds = Math.floor((ms % 60000) / 1000);
  return `${minutes}m ${seconds}s`;
};

export const ScheduledTasksPage: React.FC = () => {
  const { data: user, isLoading: isLoadingUser } = useCurrentUser();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['scheduled-tasks'],
    queryFn: async () => {
      const response = await authenticatedFetch(`${API_URL}/api/developer-tools/scheduled-tasks`);
      if (!response || !response.ok) throw new Error('Failed to fetch scheduled tasks');
      const result = await response.json();
      return result.data as ScheduledTask[];
    },
  });

  const handleRefresh = () => {
    refetch();
  };

  if (isLoadingUser) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user?.permissions?.includes('developer_tools.view')) {
    return <Navigate to="/" replace />;
  }

  const renderStatus = (params: GridRenderCellParams) => {
    const lastRun = params.row.lastRun;
    if (!lastRun) {
      return <Chip label="Never Run" size="small" color="default" />;
    }
    const colorMap: Record<string, 'success' | 'error' | 'info' | 'warning' | 'default'> = {
      completed: 'success',
      failed: 'error',
      running: 'info',
      skipped: 'warning',
    };
    return (
      <Chip
        label={lastRun.status.charAt(0).toUpperCase() + lastRun.status.slice(1)}
        size="small"
        color={colorMap[lastRun.status] || 'default'}
      />
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'functionName',
      headerName: 'Name',
      width: 240,
    },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
    },
    {
      field: 'scheduleDescription',
      headerName: 'Schedule',
      width: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      valueGetter: (_value: unknown, row: ScheduledTask) => row.lastRun?.status ?? 'never',
      renderCell: renderStatus,
    },
    {
      field: 'lastRunStartedAt',
      headerName: 'Last Run',
      width: 180,
      valueGetter: (_value: unknown, row: ScheduledTask) => row.lastRun?.startedAt || null,
      valueFormatter: (value: string | null) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      field: 'durationMs',
      headerName: 'Duration',
      width: 120,
      valueGetter: (_value: unknown, row: ScheduledTask) => row.lastRun?.durationMs ?? null,
      valueFormatter: (value: number | null) => formatDuration(value),
    },
    {
      field: 'nextRun',
      headerName: 'Next Run',
      width: 180,
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row as ScheduledTask;
        if (!row.isEnabled) {
          return <Chip label="Paused" size="small" color="warning" variant="outlined" />;
        }
        return row.nextRun ? new Date(row.nextRun).toLocaleString() : '-';
      },
      valueGetter: (_value: unknown, row: ScheduledTask) =>
        row.isEnabled ? row.nextRun : null,
    },
    {
      field: 'errorMessage',
      headerName: 'Error',
      flex: 1,
      valueGetter: (_value: unknown, row: ScheduledTask) => row.lastRun?.errorMessage || null,
      renderCell: (params: GridRenderCellParams) => {
        const errorMessage = params.row.lastRun?.errorMessage;
        if (!errorMessage) return null;
        return (
          <Tooltip title={errorMessage}>
            <Typography
              variant="body2"
              sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
            >
              {errorMessage}
            </Typography>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Scheduled Tasks</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Schedule sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} />
              <Typography variant="h4" component="h1">
                Scheduled Tasks
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={isFetching ? <CircularProgress size={20} /> : <Refresh />}
              onClick={handleRefresh}
              disabled={isFetching}
            >
              {isFetching ? 'Refreshing...' : 'Refresh'}
            </Button>
          </Box>
        </Box>

        {/* Error State */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} action={
            <Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>
          }>
            Failed to load scheduled tasks: {(error as Error).message}
          </Alert>
        )}

        {/* DataGrid */}
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={data || []}
            columns={columns}
            getRowId={(row) => row.functionName}
            slots={{ toolbar: GridToolbar }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'nextRun', sort: 'asc' }],
              },
            }}
            loading={isLoading}
            autoHeight
            disableRowSelectionOnClick
            onRowClick={(params) => navigate(`/admin/hub/system/scheduled-tasks/${params.row.functionName}`)}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />
        </Box>
      </Box>
    </Container>
  );
};

